import { useContext, useEffect, useState } from 'react';
import { COSMOS_API, POST_ENDPOINT } from '../api/API';
import { AdminContext } from '../contexts/AdminContext';
import { Admin } from '../interfaces/ApplicationInterfaces';
import AddAdmin from './AddAdmin';
import './AdminManagement.css';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Box from '@mui/material/Box';
import InputBase from '@mui/material/InputBase';
import Grid from '@mui/material/Grid';
import Checkbox from '@mui/material/Checkbox';
import Typography from "@mui/material/Typography";
import Container from "@mui/material/Container";
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { useSnackbar } from 'notistack';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import { isNullUndefEmpty } from '../ts/common';

const AdminManagement = () => {
  const [search, setSearch] = useState<string>('');
  const [results, setResults] = useState<Array<Admin>>();
  const [modifyAdmin, setModifyAdmin] = useState(false);
  const [adminToModify, setAdminToModify] = useState<Admin | undefined>();
  const {adminCredentials, getToken} = useContext(AdminContext);
  // const superAdminAppString = Object.keys(adminCredentials.allApplications)
  //   .map((key) => adminCredentials.allApplications[key])
  //   .sort()
  //   .toString();
  const superAdminAppString = 'All'
  const [organizations, setOrganizations] = useState<
    Array<{ id: string; name: string }>
  >([]);
  
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const [adminToDelete, setAdminToDelete] = useState<string>('');

  useEffect(() => {
    COSMOS_API.POST(
      { searchString: '' },
      POST_ENDPOINT.SearchEmployees,
      getToken()
    ).then((res) => setResults([...res.respBody.employees]));
    if (organizations === undefined || organizations.length === 0) {
      if (adminCredentials.isSuperAdmin) {
            setOrganizations(adminCredentials.organizationList);
      } else if (adminCredentials.isOrganizationAdmin) {
        if (adminCredentials.multiOrg) {
          setOrganizations(adminCredentials.organizationList);
        } else {
        setOrganizations([{...adminCredentials.organizationList.filter(aorg => aorg.id === adminCredentials.organizationId)[0]}])
        }
      } else {
        setOrganizations([adminCredentials.organizationList[0]]);
      }
    }
  }, [adminCredentials]);

  useEffect(() => {
    if (adminToModify !== undefined) {
      setModifyAdmin(true);
    }
  }, [adminToModify]);

  const cancelAdminMod = () => {
    setModifyAdmin(false);
    setAdminToModify(undefined);
  };

  const searchAdmins = async () => {
    setResults(undefined);
    COSMOS_API.POST(
      { searchString: search },
      POST_ENDPOINT.SearchEmployees,
      getToken()
    ).then((res) => setResults([...res.respBody.employees]));
  };

  const deleteAdmin = async () => {
    let id: string = adminToDelete;

    const deleteAdmin = await COSMOS_API.POST(
      { id: id },
      POST_ENDPOINT.DeleteEmployee,
      getToken()
    )

    if(deleteAdmin.ok) 
    {
      await searchAdmins();
      enqueueSnackbar(`Admin sucessfully deleted.`, {
        variant: 'success',
      });
    }
    else 
    {
      enqueueSnackbar(
        `Admin ${id} was not updated. If this issue perisits, please contact support.`,
        { variant: 'error' }
      );
    }

    handleClose();
  }

  const handleClose = () => {
    setAdminToDelete('');
  }

  const getAdminOrganizationsList = (admin: Admin) => {
    let orgMap = adminCredentials.organizationList.reduce((prev: {[index: string]: string}, nxt) => {
      prev[nxt.id] = nxt.name;
      return prev;
    }, {})
    let orgs = admin.administeredOrganizations?.filter(orgId => !!(orgMap[orgId])) ?? [];
    if (!orgs.includes(admin.organizationId)) {
      orgs.push(admin.organizationId);
    }
    return orgs.map(orgId => orgMap[orgId]).sort().toString();
  }

  const getAdminApplicationsList = (admin: Admin) => {
    if (admin.isSuperAdmin) {
      return superAdminAppString;
    }
    
    let adminAppList: string[] = [];
    if (admin.isOrganizationAdmin) {
      adminAppList.push("All organization applications");
      if (admin.applicationSpecificRoles) {
        Object.keys(admin.applicationSpecificRoles).map(appId => {
          if (adminCredentials.allApplications[appId]?.isGlobal) {
            adminAppList.push(adminCredentials.allApplications[appId].name);
          }
        });
      }
    } else {
      if (admin.applicationSpecificRoles) {
        adminAppList = Object.keys(admin.applicationSpecificRoles).map(appId => adminCredentials.allApplications[appId]?.name);
      }
    }
    if (adminAppList.length == 0) {
      return 'No Apps';
    } else {
      return adminAppList.sort().toString();
    }
  }

  return (
    <>
      {modifyAdmin ? <AddAdmin
        user={adminToModify}
        setUser={setAdminToModify}
        cancel={cancelAdminMod}
        searchAdmins={searchAdmins}
        modifyAdmin={modifyAdmin}
        organizations={organizations}
      />: <></>}
      <Dialog open={!isNullUndefEmpty(adminToDelete)} onClose={handleClose}>
        <DialogTitle>Delete Admin</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Remove {adminToDelete} as an admin?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>No</Button>
          <Button onClick={deleteAdmin}>Yes</Button>
        </DialogActions>
      </Dialog>
      <Container fixed>        
        <Grid container direction={'row'} spacing={4} sx={{marginBottom: '4px'}}>
          <Grid item md={6}>
      <Typography variant={"h6"}>Manage Admins</Typography>
          </Grid>
          <Grid item md={6} container>
            <Grid item xs={10} container>
            <Box flexGrow={1}></Box>
              <InputBase
          sx={{ ml: 1, flex: 1 }}
          name="search-admin"
          placeholder="Search Admins"
          inputProps={{ 'aria-label': 'search admins' }}
          onChange={(e) => {
            setSearch(e.target.value);
          }}
        />
        <IconButton type="button" sx={{ p: '10px' }} aria-label="search" onClick={searchAdmins}>
          <SearchIcon />
        </IconButton>
            </Grid>
            <Grid item xs={1}>           
      <Divider orientation="vertical" flexItem />
      <IconButton 
      color="primary" 
      sx={{ p: '10px' }} 
      aria-label="directions" 
      onClick={() => {
                setModifyAdmin(true);
                setAdminToModify(undefined);
              }}>
        <PersonAddIcon />
      </IconButton>
            </Grid>
          </Grid>
        </Grid>
        {results !== null && results !== undefined && results.length !== 0 ? (
          <TableContainer component={Paper}>
            <Table size='small'>
              <TableHead>
                <TableRow>
                  <TableCell>Email</TableCell>
                  {(adminCredentials.isSuperAdmin || adminCredentials.multiOrg) ? (
                    <TableCell>Organization</TableCell>
                  ) : (
                    <></>
                  )}
                  <TableCell>Applications</TableCell>
                  {adminCredentials.isSuperAdmin ? (
                    <TableCell>Super Admin</TableCell>
                  ) : (
                    <></>
                  )}
                  <TableCell>Remove Admin</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {results.map((admin: Admin) => {
                  return (
                    <TableRow
                      key={admin.id}
                      className="selectable-admin"
                      sx={{ cursor: 'pointer' }}
                      hover
                    >
                      <TableCell 
                      onClick={() => {
                        setAdminToModify(admin);
                      }}>{admin.id}</TableCell>
                      {(adminCredentials.isSuperAdmin || adminCredentials.multiOrg) ? (
                        admin.isSuperAdmin ?
                        <TableCell 
                        onClick={() => {
                          setAdminToModify(admin);
                        }}></TableCell>:
                        <TableCell 
                        onClick={() => {
                          setAdminToModify(admin);
                        }}>
                          {getAdminOrganizationsList(admin)}
                        </TableCell>
                      ) : (
                        <></>
                      )}
                      <TableCell 
                      onClick={() => {
                        setAdminToModify(admin);
                      }}>
                        {getAdminApplicationsList(admin)}
                      </TableCell>
                      {adminCredentials.isSuperAdmin ? (
                        <TableCell 
                        onClick={() => {
                          setAdminToModify(admin);
                        }}>
                          <Checkbox checked={admin.isSuperAdmin} readOnly />
                        </TableCell>
                      ) : (
                        <></>
                      )}
                      <TableCell>
                        {
                          admin.id !== adminCredentials.id ?
                          <IconButton aria-label="delete" size="large" onClick={() => {setAdminToDelete(admin.id)}}>
                              <DeleteIcon />
                          </IconButton> 
                          : <></>
                        }
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        ) : results === undefined ? (
          <Typography variant='h5' className="working">Loading</Typography>
        ) : (
          <Typography variant='h2' id="result-message">No Admins match the search criteria</Typography>
        )}
      </Container>
    </>
  );
};

export default AdminManagement;
